import type { Event, Property, QuoteDocument, QuoteLine, User } from '@kalos/kalos-rpc';
import { createContext, useContext } from 'react';

// Proposal status based on quote document state
export type ProposalStatus = 'draft' | 'sent' | 'signed';

// Centralized proposal calculations
export interface ProposalData {
  // Quote lines
  quoteLines: QuoteLine[];

  // Financial calculations
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  total: number;

  // Status
  status: ProposalStatus;

  // Counts
  lineItemCount: number;
}

export interface ProposalContextType {
  // Centralized proposal data with all calculations
  proposalData: ProposalData;

  // Raw data
  event: Event | null;
  customer: User | null;
  property: Property | null;
  quoteDocument: QuoteDocument | null;

  // Loading states
  isLoading: boolean;
  isQuoteDocumentLoading: boolean;
  isQuoteLinesLoading: boolean;

  // Error states
  error: string | null;

  // Refresh handlers
  refreshQuoteDocument: () => Promise<void>;
  refreshQuoteLines: () => Promise<void>;
  refreshAll: () => Promise<void>;
}

export const ProposalContext = createContext<ProposalContextType | null>(null);

export const useProposalContext = () => {
  const context = useContext(ProposalContext);
  if (!context) {
    throw new Error('useProposalContext must be used within a ProposalProvider');
  }
  return context;
};
